/* istanbul ignore file */
import React, { useContext } from 'react';
import { useTheme } from '@components/theme';
import classNames from 'classnames';
import RatingFill from '@components/shared/RatingFill/RatingFill';
import { getResizedImageUrl } from '@lib/image';
import VendorVariantContext from '@context/VendorVariant/vendor-variant-context';
import { DateTime } from 'luxon';
import { PaginatedVendorVariantReview, ReviewStats } from '@codegen-basetypes';
import styles from './Reviews.module.scss';
import SparklingIcon from './sparkling-icon.svg';
import VerifiedIcon from './verified-icon.svg';

type TReview = Pick<PaginatedVendorVariantReview['items'][number], 'ratingOverall' | 'title' | 'description' | 'created'>;
interface IReview extends TReview {
  id: number | string;
  user?: {
    name?: string;
    role?: string | null;
    designerInfo?: {
      photo?: string;
    } | null;
  } | null;
  roomType?: {
    type?: string;
  } | null;
}
interface IVendorVariantReviews {
  items: IReview[];
}
interface IReviewsProps {
  summary?: Pick<ReviewStats, 'count' | 'average'>;
  reviews?: IVendorVariantReviews | null;
  className?: string;
  reviewRef?: React.RefObject<HTMLDivElement>;
}

export default function Reviews({
  summary,
  reviews,
  className = '',
  reviewRef
}: IReviewsProps) {
  const theme = useTheme();
  const vendorVariant = useContext(VendorVariantContext);

  return (
    <div
      id="reviews"
      className={classNames(
        className,
        theme.styles.Section,
        styles.ReviewsSection
      )}
      ref={reviewRef}
    >
      <h2 className={theme.styles.Title1}>All Reviews</h2>
      <div className={styles.ReviewsContainer}>
        {
        !!summary?.count && !!reviews?.items.length && (
        <div className={styles.ReviewList}>
          <div className={styles.Summary}>
            <RatingFill
              id="rating-summary"
              rating={summary.average ?? 0}
              theme="havenly"
              size="large"
            />
            <span className={styles.ReviewCount}>
              {vendorVariant.reviewStats?.count || 0} Reviews
            </span>
          </div>
          <div className={styles.Reviews}>
            {reviews.items.map((review) => (
              <Review key={review.id} {...review} />
            ))}
          </div>
        </div>
        )
      }
        <div className={styles.Footer}>
          <a
            href={`/products/rate/${vendorVariant.id}`}
            className={classNames(
              theme.styles.Button,
              theme.styles.Primary
            )}
          >
            Leave a Review
          </a>
        </div>
      </div>
    </div>
  );
}

const SUB_RATINGS = [
  {
    text: 'Product Value',
    property: 'ratingProductValue'
  },
  {
    text: 'Product Quality',
    property: 'ratingProductQuality'
  },
  {
    text: 'Product Styles',
    property: 'ratingProductStyle'
  },
  {
    text: 'Delivery Experience',
    property: 'ratingDeliveryExperience'
  },
];

function Review(rating: IReview) {
  const {
    id,
    ratingOverall,
    title,
    description,
    user,
    created,
    roomType
  } = rating;

  const theme = useTheme();

  const avatarUrl = user!.designerInfo?.photo || null; // todo;
  const roomTypeValue = roomType?.type || null;

  return (
    <div className={styles.Review}>
      <div className={styles.Rating}>

        <div className={styles.AuthorInfo}>

          {avatarUrl && user!.role === 'designer' && (
          <div className={styles.Avatar}>
            <img
              alt=""
              src={getResizedImageUrl({
                url: avatarUrl,
                width: 80,
                height: 80,
                quality: 90
              })}
            />
          </div>
          )}

          <div className={styles.NameStarWrapper}>
            <div className={styles.NameWrapper}>
              <div className={styles.Author}>
                {user!.name}
              </div>
              <div className={styles.UserBadge}>
                {user!.role === 'designer' ? (
                  <>
                    Havenly Designer
                    <SparklingIcon />
                  </>
                ) : (
                  <>
                    Verified Buyer
                    <VerifiedIcon />
                  </>
                )}

              </div>
            </div>
            <RatingFill
              id={`rating-${id}`}
              rating={ratingOverall}
              theme="havenly"
            />
          </div>
        </div>

        <div className={styles.SubRatings}>
          {SUB_RATINGS.map(({ text, property }) => {
            const subRating = (rating as any)[property];
            return (
              subRating && (
                <React.Fragment key={property}>
                  <span>{text}</span>
                  <RatingFill
                    id={`rating-${id}-${property}`}
                    rating={subRating}
                    theme="havenly"
                  />
                </React.Fragment>
              )
            );
          })}
        </div>
      </div>
      <div className={styles.ReviewText}>
        <time className={styles.ReviewDate}>
          {DateTime.fromISO(created).toFormat('MM/DD/yy')}
        </time>
        <span className={classNames(styles.Title, theme.styles.Title3)}>{title}</span>
        <div>{description}</div>
        {
          roomTypeValue && (
            <div className={styles.RoomTypeContainer}>
              <span>In room:</span><span className={styles.RoomType}>{roomTypeValue}</span>
            </div>
          )
        }
      </div>
    </div>
  );
}
